import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, X, Loader2, Lock, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useI18n } from '../i18n';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AIAssistant() {
  const { user } = useAuth();
  const { tasks, currentWorkspace } = useWorkspace();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasAI, setHasAI] = useState(false);
  const [aiChecked, setAiChecked] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const { t } = useI18n();

  // Check AI access when workspace changes
  useEffect(() => {
    const checkAccess = async () => {
      if (!currentWorkspace) return;
      // Quick local check
      if (user?.plan === 'pro') {
        setHasAI(true);
        setAiChecked(true);
        return;
      }
      try {
        const { data } = await axios.get(`${API}/ai/access/${currentWorkspace.id}`, { withCredentials: true });
        setHasAI(data.has_ai);
      } catch {
        setHasAI(false);
      }
      setAiChecked(true);
    };
    checkAccess();
  }, [currentWorkspace, user?.plan]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const QUICK_ACTIONS = [
    { label: t('planMyDay'), prompt: t('planMyDay') },
    { label: t('prioritizeTasks'), prompt: t('prioritizeTasks') },
    { label: t('breakdownTask'), prompt: t('breakdownTask') },
  ];

  const handleSend = async (text) => {
    const msg = text || message.trim();
    if (!msg || loading) return;

    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setMessage('');
    setLoading(true);

    try {
      const taskContext = tasks.slice(0, 20).map(t => ({
        title: t.title,
        status: t.status,
        priority: t.priority,
        is_starred: t.is_starred,
        is_today_focus: t.is_today_focus,
        tag: t.tag,
      }));

      const { data } = await axios.post(`${API}/ai/chat`, {
        message: msg,
        task_context: taskContext,
        workspace_id: currentWorkspace?.id,
      }, { withCredentials: true });

      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Något gick fel';
      setMessages(prev => [...prev, { role: 'assistant', content: `Fel: ${detail}` }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 w-12 h-12 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center shadow-lg shadow-yellow-500/20 hover:shadow-yellow-500/40 hover:scale-105 transition-all z-50"
        data-testid="ai-assistant-toggle"
      >
        <Sparkles className="w-5 h-5 text-black" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-96 h-[520px] bg-[#111113] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 flex flex-col z-50 overflow-hidden" data-testid="ai-assistant-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-black" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-zinc-100 font-['Outfit']">FlowFocus AI</h3>
            <p className="text-[10px] text-zinc-500">GPT-5.2 • Pro</p>
          </div>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-zinc-500 hover:text-zinc-300 transition-colors p-1"
          data-testid="ai-assistant-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {!hasAI ? (
        /* Locked state for free users */
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-zinc-800 flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-zinc-500" />
          </div>
          <h3 className="text-base font-semibold text-zinc-100 font-['Outfit'] mb-2">
            AI-assistenten kräver Pro
          </h3>
          <p className="text-xs text-zinc-500 mb-6 leading-relaxed">
            Få hjälp att bryta ner tasks, prioritera och planera din dag med GPT-5.2.
          </p>
          <Button
            onClick={() => window.dispatchEvent(new CustomEvent('open-upgrade'))}
            className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black hover:from-yellow-300 hover:to-orange-400 h-9 px-4 text-sm font-medium"
            data-testid="ai-upgrade-btn"
          >
            Uppgradera till Pro
            <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      ) : (
        <>
          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="space-y-3">
                <p className="text-xs text-zinc-500 text-center mb-4">
                  Hej! Jag kan hjälpa dig med dina tasks.
                </p>
                {QUICK_ACTIONS.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSend(action.prompt)}
                    className="w-full text-left px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/5 hover:bg-white/[0.06] hover:border-white/10 transition-all"
                    data-testid={`ai-quick-action-${idx}`}
                  >
                    <p className="text-xs text-zinc-300">{action.label}</p>
                  </button>
                ))}
              </div>
            )}
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-xs leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-zinc-700 text-zinc-100'
                      : 'bg-white/[0.03] border border-white/5 text-zinc-300'
                  }`}
                >
                  <div className="whitespace-pre-wrap">{msg.content}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-xl bg-white/[0.03] border border-white/5">
                  <Loader2 className="w-4 h-4 text-yellow-400 animate-spin" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-white/5">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Fråga mig om dina tasks..."
                className="flex-1 bg-[#09090B] border border-white/10 rounded-lg px-3 py-2 text-xs text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
                data-testid="ai-chat-input"
              />
              <Button
                onClick={() => handleSend()}
                disabled={!message.trim() || loading}
                size="sm"
                className="h-8 w-8 p-0 bg-yellow-400 text-black hover:bg-yellow-300"
                data-testid="ai-send-btn"
              >
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
